import { useState } from 'react';
import { Check, Layers } from 'lucide-react';
import FilterService from '../../services/filterService';

function ExpenseTypeFilter({ selectedTypes = [], selectedDepartments = [], selectedExpenseGroups = [], onChange }) {
  const [isOpen, setIsOpen] = useState(false);

  // Get available expense types based on selected departments and expense groups
  const availableTypes = FilterService.getAvailableExpenseTypes(selectedDepartments, selectedExpenseGroups);

  // Expense types with descriptions
  const allExpenseTypes = [
    {
      id: 'OPEX',
      name: 'OPEX',
      fullName: 'Operational Expenditure',
      description: 'Day-to-day operating expenses',
      color: '#3b82f6',
      examples: ['Office supplies', 'Software licenses', 'Utilities']
    },
    {
      id: 'CAPEX',
      name: 'CAPEX',
      fullName: 'Capital Expenditure',
      description: 'Investments in fixed assets',
      color: '#10b981',
      examples: ['Equipment', 'Infrastructure', 'Property']
    }
  ];

  const expenseTypes = allExpenseTypes.filter(type => availableTypes.includes(type.id));

  const handleTypeToggle = (typeId) => {
    const newSelection = selectedTypes.includes(typeId)
      ? selectedTypes.filter(id => id !== typeId)
      : [...selectedTypes, typeId];
    
    onChange(newSelection);
  };

  const selectAll = () => {
    onChange(expenseTypes.map(type => type.id));
  };

  const clearAll = () => {
    onChange([]);
  };

  const getSelectedTypeNames = () => {
    return expenseTypes
      .filter(type => selectedTypes.includes(type.id))
      .map(type => type.name);
  };

  const selectedNames = getSelectedTypeNames();
  const hasSelection = selectedTypes.length > 0;

  // Show disabled state if no departments selected
  const isDisabled = selectedDepartments.length === 0;

  return (
    <div className="relative">
      {/* Type Selection Display */}
      <div 
        className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer ${
          isDisabled ? 'opacity-50 cursor-not-allowed' : ''
        }`}
        style={{ 
          background: hasSelection ? 'rgba(139, 69, 19, 0.05)' : 'var(--bg)',
          borderColor: hasSelection ? 'var(--primary)' : 'var(--border)'
        }}
        onClick={() => !isDisabled && setIsOpen(!isOpen)}
      >
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4" style={{ color: 'var(--primary)' }} />
          <span className="text-sm" style={{ color: 'var(--text)' }}>
            {isDisabled 
              ? 'Select departments first'
              : hasSelection 
                ? selectedNames.join(', ')
                : 'Select expense types'
            }
          </span>
        </div>
      </div>

      {/* Dropdown */}
      {isOpen && !isDisabled && (
        <div 
          className="absolute top-full left-0 right-0 mt-1 p-2 rounded-lg border shadow-lg z-10"
          style={{ 
            background: 'var(--panel)',
            borderColor: 'var(--border)'
          }}
        >
          {/* Header Actions */}
          <div className="flex justify-between items-center p-2 border-b mb-2" style={{ borderColor: 'var(--border)' }}>
            <span className="text-xs font-medium" style={{ color: 'var(--muted)' }}>
              Expense Types
            </span>
            <div className="flex gap-2">
              <button
                onClick={selectAll}
                className="text-xs px-2 py-1 rounded"
                style={{ 
                  background: 'var(--primary)',
                  color: 'white'
                }}
              >
                All
              </button>
              <button
                onClick={clearAll}
                className="text-xs px-2 py-1 rounded border"
                style={{ 
                  borderColor: 'var(--border)',
                  color: 'var(--text)'
                }}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Type Options */}
          <div className="space-y-2">
            {expenseTypes.map((type) => {
              const isSelected = selectedTypes.includes(type.id);
              
              return (
                <div
                  key={type.id}
                  className="p-3 rounded-md cursor-pointer border transition-colors"
                  style={{ 
                    background: isSelected ? 'rgba(139, 69, 19, 0.1)' : 'var(--bg)',
                    borderColor: isSelected ? type.color : 'var(--border)'
                  }}
                  onClick={() => handleTypeToggle(type.id)}
                >
                  <div className="flex items-start gap-3">
                    {/* Checkbox */}
                    <div 
                      className="w-5 h-5 rounded border-2 flex items-center justify-center mt-0.5"
                      style={{ 
                        borderColor: isSelected ? type.color : 'var(--border)',
                        background: isSelected ? type.color : 'transparent'
                      }}
                    >
                      {isSelected && <Check className="w-3 h-3 text-white" />}
                    </div>

                    {/* Type Info */}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ background: type.color }}
                        />
                        <span className="font-medium text-sm" style={{ color: 'var(--text)' }}>
                          {type.fullName}
                        </span>
                        <span 
                          className="px-2 py-0.5 rounded text-xs font-medium"
                          style={{ 
                            background: type.color,
                            color: 'white'
                          }}
                        >
                          {type.name}
                        </span>
                      </div>
                      
                      <p className="text-xs mb-2" style={{ color: 'var(--muted)' }}>
                        {type.description}
                      </p>
                      
                      <div className="text-xs" style={{ color: 'var(--muted)' }}>
                        <span className="font-medium">Examples: </span>
                        {type.examples.join(', ')}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* No Options Available */}
          {expenseTypes.length === 0 && (
            <div className="text-center py-4">
              <Layers className="w-8 h-8 mx-auto mb-2" style={{ color: 'var(--muted)' }} />
              <p className="text-sm" style={{ color: 'var(--muted)' }}>
                No expense types available for selected filters
              </p>
            </div>
          )}

          {/* Footer */}
          <div className="mt-3 pt-2 border-t text-center" style={{ borderColor: 'var(--border)' }}>
            <button
              onClick={() => setIsOpen(false)}
              className="text-xs px-3 py-1 rounded"
              style={{ 
                background: 'var(--border)',
                color: 'var(--text)'
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default ExpenseTypeFilter;